import type { HTMLAttributes, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import type { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';

// ---------------------------------------------------------------------------
/**
 * One row of an Autocomplete's list.
 *
 * A MULTI-value list ticks its options. The list stays open while several are
 * picked, so it has to say itself which are already in — on a phone the chips
 * sit above the open list and the keyboard, out of sight. The box is also the
 * bigger tap target, and a locked option reads as locked rather than merely
 * grey. First drawn by the Zeiterfassung's Mitarbeiter list; this is that row,
 * in one place, for every list that wants it.
 */
// ---------------------------------------------------------------------------

/** Width of the tick column, so rows without one keep the labels aligned. */
const TICK_WIDTH = 30;

export interface CheckableOptionConfig<T> {
    /** false → no tick column at all (a single-value list). */
    ticks: boolean;
    /**
     * Which options get a tick; the rest get an aligned spacer. For rows that
     * are an ACTION rather than a value — "take it as free text" is not a thing
     * that can be on or off. Default: all of them.
     */
    tickable?: (option: T) => boolean;
    /** Options that cannot be picked — a disabled tick, not just grey text. */
    disabled?: (option: T) => boolean;
    /**
     * The row's content. Optional only where the options ARE strings (a
     * vocabulary, a list of names); an object option has to say how it reads —
     * usually the same function the Autocomplete's `getOptionLabel` uses.
     */
    label?: (option: T) => ReactNode;
}

/** Build an Autocomplete `renderOption` that draws the row described above. */
export function renderCheckableOption<T>({
    ticks,
    tickable,
    disabled,
    label
}: CheckableOptionConfig<T>) {
    return function renderOption(
        props: HTMLAttributes<HTMLLIElement> & { key: unknown },
        option: T,
        state: AutocompleteRenderOptionState
    ) {
        const { key, ...liProps } = props;
        const showTick = ticks && (tickable ? tickable(option) : true);
        return (
            <Box component="li" key={key as string} {...liProps}>
                {ticks &&
                    (showTick ? (
                        <Checkbox
                            checked={state.selected}
                            disabled={disabled?.(option) ?? false}
                            size="small"
                            sx={{ mr: 1, p: 0.5 }}
                        />
                    ) : (
                        <Box sx={{ width: TICK_WIDTH, flexShrink: 0 }} />
                    ))}
                {label ? label(option) : String(option)}
            </Box>
        );
    };
}
